import { Button, Header, LoadingSpinner, WebcamPhotoCapture } from '@components/Generic'
import axios from 'axios'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'

import { API_URL } from '@/constants/apiUrl'

export const AddressUploadBothSidesIkanoPage = observer(() => {
  const [wrapperKey, setWrapperKey] = useState(new Date().getTime())

  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const { context } = store.ScenarioState
  const { AddressUploadFrontSideIkano: transFront, AddressUploadBackSideIkano: transBack } =
    store.TranslationsState.translations

  const webcamRef = useRef<WebcamPhotoCapture>(null)
  const [loading, setLoading] = useState(false)

  const [currentPhotoStep, setCurrentPhotoStep] = useState<'front' | 'back'>('front')

  const [buttonStep, setButtonStep] = useState<'take' | 'confirm'>('take')

  const [frontPhotoData, setFrontPhotoData] = useState<string | Blob | undefined>()
  const [backPhotoData, setBackPhotoData] = useState<string | Blob | undefined>()

  const handleTakePhoto = async () => {
    try {
      await webcamRef.current?.takePhoto()
      setButtonStep('confirm')
    } catch (error) {
      setErrorMessage(transFront.errorMessage)
    }
  }

  const handleSavingPhoto = async (photoData: string | Blob) => {
    if (currentPhotoStep === 'front') {
      setFrontPhotoData(photoData)
    } else {
      setBackPhotoData(photoData)
    }
  }

  const handleReset = () => {
    try {
      webcamRef.current?.reset()
      setButtonStep('take')

      if (currentPhotoStep === 'front') {
        setFrontPhotoData(undefined)
      } else {
        setBackPhotoData(undefined)
      }
    } catch (error) {
      setErrorMessage(transFront.errorMessage)
    }
  }

  const handleProceed = async () => {
    setLoading(true)

    try {
      if (currentPhotoStep === 'front' && frontPhotoData) {
        await handlePhotoUpload(frontPhotoData)
        handleReset()
        setCurrentPhotoStep('back')
      } else if (currentPhotoStep === 'back' && backPhotoData) {
        await handlePhotoUpload(backPhotoData)

        forceRedirect(`${API_URL}/pixel-speak/complete`)
      }
    } catch (error) {
      // error handling with retry button
      setErrorMessage(transFront.errorMessage)
    }

    setLoading(false)
  }

  const handlePhotoUpload = async (photoData: string | Blob) => {
    if (!context?.documentUploadUrl) return

    try {
      await axios.post(
        context?.documentUploadUrl,
        { file: photoData },
        {
          withCredentials: true,
        }
      )
    } catch (e) {
      devLog(e)
    }
  }

  const resetComponent = () => {
    setLoading(false)
    setCurrentPhotoStep('front')
    setButtonStep('take')
    setFrontPhotoData(undefined)
    setBackPhotoData(undefined)
    setErrorMessage(undefined)
    webcamRef.current?.reset()

    setWrapperKey(new Date().getTime())
  }

  return (
    <div className="page-container" key={wrapperKey}>
      {
        <>
          {/* Headline */}
          <Header>{currentPhotoStep === 'front' ? transFront.header : transBack.header}</Header>

          {!errorMessage?.length && (
            <>
              {buttonStep === 'confirm' ? (
                <div className="generic__paragraph">
                  {currentPhotoStep === 'front'
                    ? transFront.descriptionConfirm
                    : transBack.descriptionConfirm}
                </div>
              ) : (
                <div className="generic__paragraph">
                  {currentPhotoStep === 'front' ? transFront.description : transBack.description}
                </div>
              )}
            </>
          )}

          {/* Camera Component */}
          {!errorMessage?.length ? (
            <div className="generic__loading-overlap-wrapper">
              <WebcamPhotoCapture
                ref={webcamRef}
                showFrame
                onPhotoCapture={(value) => handleSavingPhoto(value)}
                outputFormat="image/jpeg"
              />

              {loading && (
                <div className="generic__loading-overlap">
                  <div className="layout__margined-with-height">
                    <Header>{transFront.verifying}</Header>
                  </div>
                  <LoadingSpinner />
                </div>
              )}
            </div>
          ) : (
            <div className="page-container p-20-0 w-100percent">
              <p className="generic__paragraph-centered">{errorMessage}</p>
              <div className="layout__centered-content">
                <Button onClick={resetComponent}>{transFront.errorRetry}</Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!errorMessage?.length && (
            <div className="layout__content-container-bottom-fixed layout__content-container-bottom-fixed--gap-16">
              {buttonStep === 'confirm' ? (
                <>
                  <Button className="button--secondary" onClick={handleReset}>
                    {transFront.retakePhoto}
                  </Button>

                  <Button onClick={() => handleProceed()}>
                    <span className="generic__button-text">{transFront.proceed}</span>
                  </Button>
                </>
              ) : (
                <>
                  {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
                  <Button onClick={handleTakePhoto}>{transFront.takePhoto}</Button>
                </>
              )}
            </div>
          )}
          <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
        </>
      }
    </div>
  )
})
